import { CLAIMS, PermissionClaims } from './claims';

export enum Role {
  CompanyAdmin = 'CompanyAdmin',
  CompanyUser = 'CompanyUser'
}

export type ClaimRule = {
  claims: string[];
  isBeta?: boolean;
};

/**
 * @deprecated Role-based access is the legacy pattern — it predates per-capability permission claims.
 * Do not add new role rules; model new capabilities as claim rules via {@link anyClaim} / {@link anyOf}
 * backed by {@link PermissionClaims}. The remaining role rules are quarantined in {@link legacyRoleRules}.
 */
export type RoleRule = {
  viewRole: Role;
  editRole?: Role;
};

export type AccessRule = ClaimRule | RoleRule;

export const permission = (claim: PermissionClaims): string => `permissions_${claim.toLowerCase()}`;

export const anyClaim = (...claims: PermissionClaims[]): ClaimRule => ({
  claims: claims.map(permission)
});

export const anyOf = (...composed: ClaimRule[]): ClaimRule => ({
  claims: composed.flatMap((rule) => rule.claims),
  isBeta: composed.some((rule) => rule.isBeta) || undefined
});

/**
 * @deprecated Builds a legacy role-based rule. Prefer claim rules ({@link anyClaim} / {@link anyOf}).
 */
export const roleRule = (viewRole: Role, editRole?: Role): RoleRule => ({ viewRole, editRole });

const module = {
  pgap: { claims: [CLAIMS.features.pgap] },
  reviews: { claims: [CLAIMS.features.review] },
  ivr: { claims: [CLAIMS.features.ivr] },
  etrack1: { claims: [CLAIMS.management.etrack1] }
} satisfies Record<string, ClaimRule>;

const order = {
  read: { claims: [permission(PermissionClaims.OrderRead)], isBeta: true },
  create: anyClaim(PermissionClaims.OrderCreate),
  update: anyClaim(PermissionClaims.OrderUpdate),
  delete: anyClaim(PermissionClaims.OrderDelete)
} satisfies Record<string, AccessRule>;

const apiKey = {
  read: anyClaim(
    PermissionClaims.ApiKeyRead,
    PermissionClaims.ApiKeyCreate,
    PermissionClaims.ApiKeyUpdate,
    PermissionClaims.ApiKeyDelete
  ),
  create: anyClaim(PermissionClaims.ApiKeyCreate),
  update: anyClaim(PermissionClaims.ApiKeyUpdate, PermissionClaims.ApiKeyCreate),
  delete: anyClaim(PermissionClaims.ApiKeyDelete, PermissionClaims.ApiKeyCreate)
} satisfies Record<string, AccessRule>;

const user = {
  read: anyClaim(
    PermissionClaims.UserRead,
    PermissionClaims.UserCreate,
    PermissionClaims.UserUpdate,
    PermissionClaims.UserDelete
  ),
  create: anyClaim(PermissionClaims.UserCreate),
  update: anyClaim(PermissionClaims.UserUpdate),
  delete: anyClaim(PermissionClaims.UserDelete)
} satisfies Record<string, AccessRule>;

const tenant = {
  read: anyClaim(
    PermissionClaims.TenantRead,
    PermissionClaims.TenantUpdate,
    PermissionClaims.TenantDelete
  ),
  create: anyClaim(PermissionClaims.TenantCreate),
  update: anyClaim(PermissionClaims.TenantUpdate)
} satisfies Record<string, AccessRule>;

const childTenant = {
  read: anyClaim(PermissionClaims.ChildTenantRead),
  update: anyClaim(PermissionClaims.ChildTenantUpdate),
  manageAccess: anyClaim(PermissionClaims.TenantUserAccessCrud)
} satisfies Record<string, AccessRule>;

const organization = {
  manage: anyClaim(
    PermissionClaims.TenantCreate,
    PermissionClaims.ChildTenantRead,
    PermissionClaims.ChildTenantUpdate,
    PermissionClaims.ChildTenantDelete
  )
} satisfies Record<string, AccessRule>;

const sender = {
  read: anyClaim(PermissionClaims.SenderRead),
  create: anyClaim(PermissionClaims.SenderCreate),
  update: anyClaim(PermissionClaims.SenderUpdate),
  delete: anyClaim(PermissionClaims.SenderDelete)
} satisfies Record<string, AccessRule>;

const response = {
  update: anyClaim(PermissionClaims.ResponseUpdate)
} satisfies Record<string, AccessRule>;

const brandedLink = {
  create: anyClaim(PermissionClaims.BrandedLinkCreate),
  update: anyClaim(PermissionClaims.BrandedLinkUpdate),
  delete: anyClaim(PermissionClaims.BrandedLinkDelete)
} satisfies Record<string, AccessRule>;

const provider = {
  manage: anyClaim(PermissionClaims.ProviderManage)
} satisfies Record<string, AccessRule>;

const featureFlag = {
  manage: anyClaim(PermissionClaims.FeatureFlagManage)
} satisfies Record<string, AccessRule>;

const orderType = {
  manage: anyClaim(PermissionClaims.OrderTypeManage)
} satisfies Record<string, AccessRule>;

const aiSupport = {
  config: anyClaim(PermissionClaims.AiSupportConfig)
} satisfies Record<string, AccessRule>;

const aiFeedback = {
  read: anyClaim(PermissionClaims.AiFeedbackResponseRead)
} satisfies Record<string, AccessRule>;

const tokenUsage = {
  readGlobal: anyClaim(PermissionClaims.GlobalTokenUsageLogRead)
} satisfies Record<string, AccessRule>;

const claimAssignment = {
  tenant: anyClaim(PermissionClaims.TenantClaimsAssign),
  any: anyClaim(PermissionClaims.TenantClaimsAssign, PermissionClaims.GlobalClaimsAssign)
} satisfies Record<string, AccessRule>;

const campaign = {
  editSurveyJs: { claims: [CLAIMS.beta, CLAIMS.management.surveyjs] }
} satisfies Record<string, ClaimRule>;

export const rules = {
  module,
  order,
  apiKey,
  user,
  tenant,
  childTenant,
  organization,
  sender,
  response,
  brandedLink,
  provider,
  featureFlag,
  orderType,
  aiSupport,
  aiFeedback,
  tokenUsage,
  claimAssignment,
  campaign,
  adminArea: anyOf(organization.manage, aiSupport.config, featureFlag.manage, orderType.manage)
};

/**
 * @deprecated Legacy role-based rules. Do not add new entries — rework these into claim rules once the
 * backend exposes a matching permission claim.
 *
 * - `adminManage`: visible to a CompanyUser but only editable by a CompanyAdmin (the guard renders the
 *   control read-only for non-admins).
 * - `adminOnly`: visible only to a CompanyAdmin.
 */
export const legacyRoleRules = {
  adminManage: roleRule(Role.CompanyUser, Role.CompanyAdmin),
  adminOnly: roleRule(Role.CompanyAdmin)
};
